import Link from "next/link";

import Image from "next/image";
import instagramLogo from "@/img/instagram.ico";
import facebookLogo from "@/img/facebook.ico";
import twitterLogo from "@/img/twitter.ico";
import linkedinLogo from "@/img/linkedin.ico";
import youtubeLogo from "@/img/youtube.ico";
import blackLogo from "@/img/black-logo.png";
import tiktokLogo from "@/img/tiktok.ico";
import { FC } from "react";
import useMobileDetect from "hooks/useMobileDetect";
import type { NavigationItem } from "../../lib/gtm";
import * as gtm from "../../lib/gtm";

const footerLinks: Array<{
  name: string;
  href: string;
  analyticsItem?: NavigationItem;
}> = [
  { name: "Home", href: "/" },
  {
    name: "Community",
    href: "https://www.facebook.com/groups/getyoudle/",
    analyticsItem: "community_social",
  },
  {
    name: "Grocery News",
    href: "https://news.youdle.io/",
    analyticsItem: "news",
  },
  { name: "About Us", href: "/about-us" },
  // { name: "Sell", href: "/sell" },
  // { name: "Watchlist", href: "/watchlist" },
  // { name: "Account", href: "/signin" },
  { name: "Privacy & Terms of Service", href: "/privacy-policy" },
  {
    name: "Help Build Youdle",
    href: "/community",
  },
];

const Footer = () => {
  return (
    // gray-400 (#E0E0E0) is the palette's nearest shade to the gray-300 this
    // asked for; gray-300 is not defined, so the band never rendered at all.
    <footer className="mt-16 w-full bg-gray-400 text-black" id="footer">
      <div className="mx-auto w-full max-w-6xl px-6 py-12 lg:px-12">
        <FooterContent />
        <div className="mt-10 border-t border-black/10 pt-6 text-center text-sm font-thin">
          <p className="m-0">
            Copyright &copy; Youdle {new Date().getFullYear()}
          </p>
          <p>All Rights Reserved</p>
        </div>
      </div>
    </footer>
  );
};

const FooterContent: FC = () => {
  const { isMobile } = useMobileDetect();
  if (isMobile) {
    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col items-center gap-6">
          <Image
            src={blackLogo}
            alt="Youdle logo"
            className="h-auto w-40 max-w-full"
          />
          <Socials className="justify-center" />
        </div>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <LinkCol footerLinks={footerLinks.slice(0, 4)} />
          <LinkCol footerLinks={footerLinks.slice(4, 8)} />
          <LinkCol footerLinks={footerLinks.slice(8)} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-10">
      <div className="flex w-full flex-wrap justify-center gap-12 lg:gap-16">
        <div className="flex min-w-[220px] flex-col items-center gap-6 text-center">
          <Image
            src={blackLogo}
            alt="Youdle logo"
            className="h-auto w-48 max-w-full"
          />
          <Socials className="justify-center" />
        </div>
        <div className="grid grid-cols-1 gap-8 text-center sm:grid-cols-2 md:text-left">
          <LinkCol footerLinks={footerLinks.slice(0, 4)} />
          <LinkCol footerLinks={footerLinks.slice(4, 8)} />
          <LinkCol footerLinks={footerLinks.slice(8)} />
        </div>
      </div>
    </div>
  );
};

interface LinkColProps {
  footerLinks: {
    name: string;
    href: string;
    analyticsItem?: NavigationItem;
  }[];
  className?: string;
}

const LinkCol: FC<LinkColProps> = ({ footerLinks, className = '' }) => {
  return (
    <div className={`flex flex-col gap-3 text-black text-center md:text-left ${className}`}>
      {footerLinks.map((link) => (
        <Link
          href={link.href}
          key={link.name}
          className="transition-colors duration-300 hover:text-red-600 hover:underline"
          onClick={() => {
            if (link.analyticsItem) {
              gtm.navigationClicked({
                item: link.analyticsItem,
                location: "footer",
                destination: link.href,
                control: "footer_link",
              });
            }
          }}
        >
          {link.name}
        </Link>
      ))}
    </div>
  );
};

const socialLogos = [
  {
    name: "Instagram",
    img: instagramLogo,
    url: "https://instagram.com/getyoudle",
  },
  {
    name: "Facebook",
    img: facebookLogo,
    url: "https://facebook.com/groups/getyoudle",
  },
  { name: "Twitter", img: twitterLogo, url: "https://twitter.com/getyoudle" },
  {
    name: "LinkedIn",
    img: linkedinLogo,
    url: "https://www.linkedin.com/company/youdle/",
  },
  { name: "Youtube", img: youtubeLogo, url: "https://www.youtube.com/@youdle" },
  { name: "TikTok", img: tiktokLogo, url: "https://www.tiktok.com/@getyoudle" },
];

const Socials: FC<{ className?: string }> = ({ className = "" }) => {
  return (
    <div
      className={`flex flex-wrap gap-3 fill-black text-black ${className}`}
    >
      {socialLogos.map((social) => (
        <Link
          key={social.name}
          href={social.url}
          className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-black transition-all duration-300 hover:border-red-600 hover:bg-red-100 hover:scale-110"
        >
          <Image
            src={social.img}
            alt={social.name}
            width={24}
            height={24}
            className="max-w-6 max-h-6"
          />
        </Link>
      ))}
    </div>
  );
};

export default Footer;
